import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useChatState } from '../../AIChat/hooks/useChatState';
import { useChatPersistence } from '../../AIChat/hooks/useChatPersistence';
import { useStreamingResponse } from '../../AIChat/hooks/useStreamingResponse';
import { useToolExecution } from '../../AIChat/hooks/useToolExecution';
import { useProactiveInsights, ProactiveInsight } from '../../AIChat/hooks/useProactiveInsights';
import { ChatMessage, ToolCallMessage } from '../../AIChat/types';
import { parseRecommendations } from '../../AIChat/utils/parsers';
import { V2_STORAGE_KEY, V2_SYSTEM_PROMPT, ERROR_MESSAGES } from '../constants';
import { usePortfolioTier } from './usePortfolioTier';
import { useContextBuilders } from './useContextBuilders';
import { PortfolioTier } from '../services/tierConfig';

const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

interface UseChatV2Return {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  tier: PortfolioTier;
  contextLoading: boolean;
  insights: ProactiveInsight[];
  unreadInsightCount: number;
  dismissInsight: (id: string) => void;
  sendMessage: (content: string) => Promise<void>;
  clearHistory: () => void;
  clearError: () => void;
}

export const useChatV2 = (): UseChatV2Return => {
  const [isProcessing, setIsProcessing] = useState(false);
  const messageIdRef = useRef<string>('');
  const pendingToolCallsRef = useRef<ToolCallMessage[]>([]);
  const sentMessagesRef = useRef<ChatMessage[]>([]);

  // V2-specific hooks
  const { tier, configOverride } = usePortfolioTier();
  const { context, isLoading: contextLoading } = useContextBuilders();

  // Proactive insights (from V1 intelligence layer)
  const { insights, unreadCount, dismissInsight } = useProactiveInsights({ enabled: true });

  // V1 shared hooks
  const {
    state,
    addUserMessage,
    startStreaming,
    appendToken,
    addToolCall,
    addToolResult,
    completeStreaming,
    clearMessageContent,
    setError,
    clearError,
    clearHistory: clearChatState,
    loadMessages,
  } = useChatState();

  const {
    loadMessages: loadPersistedMessages,
    saveMessages,
    clearMessages,
  } = useChatPersistence({ storageKey: V2_STORAGE_KEY });

  const { executeTools } = useToolExecution();

  // Build the final system prompt with context + active insights
  const systemPrompt = useMemo(() => {
    let prompt: string;

    if (!context) {
      const loadingMsg =
        'Portfolio data is still loading. Do NOT guess any portfolio values. Tell the user to wait a moment and try again.';
      prompt = V2_SYSTEM_PROMPT.replace('{PORTFOLIO_CONTEXT}', loadingMsg)
        .replace('{TRANSACTION_CONTEXT}', 'Transaction data is not yet available.')
        .replace('{VALUATION_CONTEXT}', 'Valuation data is not yet available.');
    } else {
      prompt = V2_SYSTEM_PROMPT.replace('{PORTFOLIO_CONTEXT}', context.portfolioContext)
        .replace('{TRANSACTION_CONTEXT}', context.transactionContext)
        .replace('{VALUATION_CONTEXT}', context.valuationContext);
    }

    // Inject active insights so the AI is aware of current alerts
    if (insights.length > 0) {
      const insightLines = insights.map((i) => `- [${i.priority.toUpperCase()}] ${i.title}: ${i.description}`);
      prompt += `\n\n## ACTIVE PORTFOLIO ALERTS (from real-time monitoring)
The following alerts have been detected from live portfolio data. Reference these proactively when relevant:

${insightLines.join('\n')}

When the user asks general questions like "how is my portfolio?" or "anything I should know?", mention relevant active alerts.`;
    }

    return prompt;
  }, [context, insights]);

  // Process tool calls and continue conversation
  const processToolCallsAndContinue = useCallback(
    async (toolCalls: ToolCallMessage[], previousMessageId: string, currentMessages: ChatMessage[]) => {
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        const results = await executeTools(toolCalls);
        const messagesWithResults: ChatMessage[] = [...currentMessages];

        for (const result of results) {
          addToolResult(result);
          messagesWithResults.push({
            id: `tool_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
            role: 'tool',
            content: JSON.stringify(result.result),
            timestamp: Date.now(),
            toolResult: result,
          });
        }

        sentMessagesRef.current = messagesWithResults;
        clearMessageContent(previousMessageId);

        await streamingResponse.sendMessage(messagesWithResults, previousMessageId);
      } catch (error) {
        setError(ERROR_MESSAGES.toolExecutionFailed);
        completeStreaming(messageIdRef.current, []);
      } finally {
        setIsProcessing(false);
        pendingToolCallsRef.current = [];
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isProcessing, executeTools, addToolResult, clearMessageContent, completeStreaming, setError],
  );

  // Streaming response handler
  const streamingResponse = useStreamingResponse({
    systemPrompt,
    configOverride,
    onToken: (messageId, token) => {
      appendToken(messageId, token);
    },
    onToolCall: (messageId, toolCall) => {
      addToolCall(messageId, toolCall);
      pendingToolCallsRef.current.push(toolCall);
    },
    onComplete: async (messageId, fullResponse) => {
      const recommendations = parseRecommendations(fullResponse);

      if (pendingToolCallsRef.current.length > 0) {
        const baseMessages = sentMessagesRef.current;

        const assistantMessage: ChatMessage = {
          id: messageId,
          role: 'assistant',
          content: fullResponse,
          timestamp: Date.now(),
          toolCalls: [...pendingToolCallsRef.current],
        };

        const messagesWithToolCalls = [...baseMessages, assistantMessage];
        await processToolCallsAndContinue(pendingToolCallsRef.current, messageId, messagesWithToolCalls);
      } else {
        completeStreaming(messageId, recommendations);
      }
    },
    onError: (error) => {
      setError(error || ERROR_MESSAGES.unknown);
      completeStreaming(messageIdRef.current, []);
    },
  });

  // Load persisted messages on mount
  useEffect(() => {
    const persisted = loadPersistedMessages();
    if (persisted.length > 0) {
      loadMessages(persisted);
    }
  }, [loadPersistedMessages, loadMessages]);

  // Save messages when they change
  useEffect(() => {
    if (state.messages.length > 0) {
      saveMessages(state.messages);
    }
  }, [state.messages, saveMessages]);

  // Handle sending a message
  const handleSendMessage = useCallback(
    async (content: string) => {
      if (state.isStreaming || isProcessing) return;

      addUserMessage(content);

      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: Date.now(),
      };

      const filteredMessages = state.messages.filter((msg) => {
        if (msg.role === 'system') return false;
        if (msg.role === 'tool') return false;
        if (msg.role === 'assistant' && !msg.content && !msg.isStreaming) return false;
        return true;
      });
      const allMessages = [...filteredMessages, userMessage];

      const assistantMessageId = generateMessageId();
      messageIdRef.current = assistantMessageId;
      pendingToolCallsRef.current = [];
      sentMessagesRef.current = allMessages;

      startStreaming(assistantMessageId);

      try {
        await streamingResponse.sendMessage(allMessages, assistantMessageId);
      } catch (error) {
        setError(ERROR_MESSAGES.unknown);
        completeStreaming(assistantMessageId, []);
      }
    },
    [
      state.isStreaming,
      state.messages,
      isProcessing,
      addUserMessage,
      startStreaming,
      streamingResponse,
      setError,
      completeStreaming,
    ],
  );

  // Handle clear history
  const handleClearHistory = useCallback(() => {
    clearChatState();
    clearMessages();
  }, [clearChatState, clearMessages]);

  return {
    messages: state.messages,
    isStreaming: state.isStreaming || isProcessing,
    error: state.error,
    tier,
    contextLoading,
    insights,
    unreadInsightCount: unreadCount,
    dismissInsight,
    sendMessage: handleSendMessage,
    clearHistory: handleClearHistory,
    clearError,
  };
};
